import appConstants from '../../../../../../../appConstants';
import { PICKUP_STATUS } from '../../../constants';
import { filterPickup, hasPriority } from '../../../filterUtils';
import { getDuplicatePickupsByPickupId, isPossibleMergeFilterApplied } from '../../../../utils/utils';
import { searchInData, isGroupHeaders } from '../utils';
import { RMAXLogger } from '../../../../../../../services/LoggerService/RMAXLogger';
import {
  PICKUPS_GRID_FILTERS,
  DEFAULT_DISPATCH_GRID_FILTER_LIST
} from '../../Grid/Filter/DEFAULT_constants';

/**
 * FilterManager - Handles all filtering-related logic for GroupPickup component
 * 
 * This manager handles:
 * - External filter (header filter, panel filter, search text)
 * - Status bar construction
 * - Filter change events
 */
export class FilterManager {
  constructor(component) {
    this.component = component;
  }

  onSearchTextChanged = (searchText) => {
    this.component.setState(
      {
        quickFilterText: searchText
      },
      () => {
        // Trigger external filter to apply search
        if (this.component.gridApi && !this.component.isGroupSelectionInProgress) {
          this.component.gridApi.onFilterChanged();
        }
      }
    );
  };

  headerFilterChanged = ({ option, isPickupFilterMounted }) => {
    // Clear all selections when filter changes
    this.component.gridApi?.deselectAll?.(); // Clear grid selections
    this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [[]]); // Clear store selections
    this.component.props.glEventHub.emit(appConstants.Event.PICKUP_SELECTED_GRID, {
      pickupsData: []
    });
    this.component.updatePickupManagementIconStatus();
    // Clear pending edit flags when filter changes
    this.component.pendingEditPickup = false;
    this.component.pendingEditPickupId = null;

    this.component.setState(
      {
        headerFilter: option
      },
      () => this.component.gridApi.onFilterChanged?.()
    );
    this.component.props.glEventHub.emit('pickupsGridFilterChanged', option);
    this.triggerAutoPickupRecommendationOnFilterChange(option, isPickupFilterMounted);
    if(option.value === PICKUPS_GRID_FILTERS.toBeReviewed) {
      RMAXLogger.info('pickupsGridFilterChanged', {action:"pickupsGridFilterChanged", value:option.value});
    }
  };

  triggerAutoPickupRecommendationOnFilterChange = (filterOption, isPickupFilterMounted) => {
    if (!this.component.isRecommendationsEnabled || isPickupFilterMounted) {
      //we should refresh recommendation only if filter changed by user not for pickup filter mount
      return;
    }
    if (this.component.showHideRecommendationColumn) {
      this.component.showHideRecommendationColumn(filterOption, true);
    }
  };

  panelFilterChanged = (filter) => {
    if (filter === 'toBeReviewed') {
      const toBeReviewedOption = DEFAULT_DISPATCH_GRID_FILTER_LIST.find(
        (f) => f.value === PICKUPS_GRID_FILTERS.toBeReviewed
      );
      if (toBeReviewedOption) {
        this.component.props.glEventHub.emit('pickupsPanelfilterchanged', toBeReviewedOption);
      }
    }
    this.component.setState(
      {
        panelFilter: filter
      },
      () => {
        // Force re-render to apply the new filter
        this.component.forceUpdate();
        this.component.gridApi.onFilterChanged?.();
      }
    );
  };

  doesExternalFilterPass = (node) => {
    // Group headers should be shown if they have any children that pass the filter
    if (isGroupHeaders(node.data)) {
      // Check if this group has any children that would pass the current filters
      const groupId = node.data.id;
      const allPickups = this.component.props.DispatchStore.pickupsList;
      const groupChildren = allPickups.filter((pickup) => pickup.groupId === groupId);

      // If any child passes the filter, show the group header
      return groupChildren.some((child) => {
        const mockNode = { data: child };
        return this.doesPickupPassFilter(mockNode);
      });
    }

    // For pickup rows, apply all filters
    return this.doesPickupPassFilter(node);
  };

  // Helper method to check if a pickup passes the header filter only (used for status bar counts)
  doesHeaderFilterPass = (node) => {
    let doesFilterPass = true;

    // Handle header filter (same as regular Pickups Grid)
    if (this.component.state.headerFilter && this.component.state.headerFilter.value) {
      const isPossibleMergeFilter = isPossibleMergeFilterApplied(this.component.state.headerFilter.value);
      doesFilterPass = filterPickup({
        pickup: node.data,
        ...(isPossibleMergeFilter && {
          duplicatePickups: getDuplicatePickupsByPickupId(
            this.component.props.DispatchStore.duplicatePickups,
            node.data.pickupId
          )
        }),
        pickupFilter: this.component.state.headerFilter,
        terminalOffset: this.component.props.DispatchStore.terminalOffset
      });
    }

    return Boolean(doesFilterPass);
  };

  // Helper method to check if a pickup passes all current filters
  doesPickupPassFilter = (node) => {
    let doesFilterPass = this.doesHeaderFilterPass(node);

    // Handle panel filters (same as regular Pickups Grid)
    if (this.component.state.panelFilter === 'guaranteed') {
      doesFilterPass =
        doesFilterPass &&
        hasPriority(node.data.priority, this.component.props.DispatchStore.priorityShipmentList);
    } else if (this.component.state.panelFilter === 'swaps') {
      doesFilterPass = doesFilterPass && node.data.isSwapPickup;
    } else if (this.component.state.panelFilter === 'priority') {
      doesFilterPass = doesFilterPass && node.data.pickupPriority;
    } else if (this.component.state.panelFilter === 'closingSoon') {
      doesFilterPass = doesFilterPass && this.component.getClosingSoonPickupTime(node.data.pickupId) !== null;
    }

    // Handle search text using optimized utility function
    if (this.component.state.quickFilterText && doesFilterPass) {
      return searchInData(node.data, this.component.state.quickFilterText);
    }

    return doesFilterPass;
  };

  constructStatusBar = (filteredData) => {
    // Filter pickups based on header filter only (not panel filter) to keep counts consistent
    // when a panel filter is selected
    const allPickups = this.component.props.DispatchStore.pickupsList.filter(
      (pickup) => !isGroupHeaders(pickup)
    );
    
    // Apply only header filter (and column filter if present) for status bar counts
    const filteredPickups = this.component.gridApi?.isColumnFilterPresent?.()
      ? filteredData.filter((data) => !isGroupHeaders(data))
      : allPickups.filter((pickup) => this.doesHeaderFilterPass({ data: pickup }));

    return [
      {
        label: filteredPickups.length + ' Pickups',
        filterCondition: PICKUP_STATUS.allPickups
      },
      {
        label:
          filteredPickups.filter((pickup) =>
            hasPriority(pickup.priority, this.component.props.DispatchStore.priorityShipmentList)
          ).length + ' Guaranteed',
        filterCondition: 'guaranteed'
      },
      {
        label:
          filteredPickups.filter((pickup) =>
            this.component.props.DispatchStore.pickupIdToClosingTimeMapping.hasOwnProperty(
              pickup.pickupId?.toUpperCase()
            )
          ).length + ' Closing soon',
        filterCondition: 'closingSoon'
      },
      {
        label: filteredPickups.filter((pickup) => pickup.isSwapPickup).length + ' Swaps',
        filterCondition: 'swaps'
      },
      {
        label: filteredPickups.filter((pickup) => pickup.pickupPriority).length + ' Priority',
        filterCondition: 'priority'
      },
      {
        label: filteredPickups.filter((pickup) => pickup.isToBeReviewed).length + ' To Be Reviewed',
        filterCondition: 'toBeReviewed'
      }
    ];
  };
}

